import React, { useMemo, useRef, useEffect, useCallback } from "react";
import MultiSelectMenu from "./MultiSelectMenu";
import clsx from "clsx";
import {
  getSourceQueryKey,
  getSourceQueryKeyPrefix,
  INTERESTING_THINGS_QUERY_KEY,
  TAGGED_INSTANCES_QUERY_KEY,
  updateYoutubeCacheWithPage,
} from "../domain_data";
import { useQueryClient } from "@tanstack/react-query";
import {
  YouTubeTranscriptLineView,
  YoutubeSourceLocation,
  SourceLocationType,
  TupleIntegerInteger,
  getPageForTimestamp,
  YouTubePageView,
  useReprocessYoutubePage,
  updateLastViewedPage,
  getYoutubePage,
  useGetYoutubePage,
} from "../api-generated";
import YouTubeLines from "./YouTubeLines";
import SourceHeader from "./SourceHeader";
import {
  getYouTubeCopyLinkOption,
  isWordToken,
  isInRangeOfArray,
  useSourceUrlDetails,
  formatTimestamp,
  isYoutubeSource,
  isInRange,
} from "../utils";
import { useKeyPressEvent, useNavigableLines } from "../hooks";
import EmbeddedYoutubeVideo, {
  useEmbeddedYoutubeVideo,
} from "./EmbeddedYoutubeVideo";
import PaginationControls from "./PaginationControls";
import CenteredLoading from "./Loading";
import ErrorLoading from "./ErrorLoading";
import PlayBackControls from "./PlaybackControls";
import { NavLine } from "../hooks/useNavigableLines";
import { usePrefetchAdjacentPages } from "../hooks";
import useMultiSelect, { MultiSelectableBlob } from "../hooks/useMultiSelect";

interface PlayingIndexIsh {
  lineIndex: number;
  tokenIndeces: number[];
}

interface Props {
  source: YouTubePageView;
  isFetching: boolean;
  onChangePage: (pageNb: number) => void;
  currentPageNb: number;
  navToPageAtTs: (ts: number) => void;
}

const YouTubePage: React.FC<Props> = ({
  source,
  isFetching,
  onChangePage,
  currentPageNb,
  navToPageAtTs,
}: Props) => {
  const {
    currentTime,
    seekTo,
    onPlayerReady,
    onPlayerStateChange,
    isPlayingOrBuffering,
    playPause,
  } = useEmbeddedYoutubeVideo();

  // *** PAGINATION/PLAYER STUFF ***
  const navToPageStartTsOnVideoPlayRef = useRef<boolean>(true);
  const isNavingToPageAtSpecificTsRef = useRef<boolean>(false);
  const videoBufferingAfterSeek = useRef<boolean>(false);
  const lastPageRef = useRef<number | undefined>(undefined);

  const seekToPageStart = useCallback((pageNumber: number) => {
    //TODO: not sure pageNumber has to be an arg... setting it as one atow
    //b/c I'm worried closure will have old pageNumber
    const pageRange: TupleIntegerInteger | undefined = source.pageTsRange;
    if (pageRange === undefined) {
      throw new Error(`couldn't get a range of ts for page ${pageNumber}`);
    }
    console.log(
      `YoutubePage calling EmbeddedYoutubeVideo.seekTo to start of page ${pageNumber} - ${pageRange.first}`
    );
    videoBufferingAfterSeek.current = true;
    seekTo(pageRange.first!)
      .then(() => (videoBufferingAfterSeek.current = false))
      .catch((err) => {
        console.error(err);
        videoBufferingAfterSeek.current = false;
      });
  }, []);

  useEffect(() => {
    //SEEKING TO TS BC PAGE CHANGES
    //- when user changes page
    //    - this can be when video is paused (or hasn't started yet) - set up flag for when it starts
    //    - or if the video is playing - seek to now
    //- UNLESS the page was changed programatically b/c TS has changed
    if (source.pageNb === lastPageRef.current) {
      // this check b/c we want rest of function to run when page actually changes, not just when effect fires
      return;
    }
    lastPageRef.current = source.pageNb;

    const newPageRange: TupleIntegerInteger = source.pageTsRange;
    console.log(
      `PAGE ACTUALLY CHANGED TO: ${source.pageNb}, with range ${newPageRange.first} - ${newPageRange.second}`
    );

    if (isNavingToPageAtSpecificTsRef.current === true) {
      console.log(
        "currently naving to page at specific ts, not going to do anything"
      );
      isNavingToPageAtSpecificTsRef.current = false;
      return;
    }

    if (!isPlayingOrBuffering) {
      console.log(
        "page changed, but not playing right now, so setting it up to seek for when video starts again"
      );
      navToPageStartTsOnVideoPlayRef.current = true;
      return;
    }

    console.log(
      `seeking to start of page ${source.pageNb} b/c user changed page while video playing`
    );
    seekToPageStart(source.pageNb);
  }, [source.pageNb, isPlayingOrBuffering]);

  const handlePlayerPlay = (): void => {
    console.log("Youtube page notified that player started playing again");
    if (navToPageStartTsOnVideoPlayRef.current) {
      console.log(
        `seeking to start of page ${source.pageNb} b/c page has changed since being paused, or b/c this is the first time the video is playing`
      );
      navToPageStartTsOnVideoPlayRef.current = false;
      seekToPageStart(source.pageNb);
    }
  };

  const currentPageTsRange: TupleIntegerInteger = source.pageTsRange;

  useEffect(() => {
    //CHANGING PAGE BC TS CHANGES
    //- ts can change b/c video is playing
    //- or b/c user seeks ahead in video
    //- page changes happens immediately in any case
    //- don't have to worry about ts changing when seeking to beginning of page - ts in this case won't be out of page range
    if (currentTime === undefined || currentTime === 0) {
      return;
    }

    if (videoBufferingAfterSeek.current === true) {
      console.log(
        `ts changed to ${currentTime}, but video still buffering after seeking, so not doing anything`
      );
      return;
    }

    if (navToPageStartTsOnVideoPlayRef.current === true) {
      console.log(
        `ts changed to ${currentTime}, but video is set to seek to new page ts when video stars`
      );
      return;
    }

    if (
      !isNavingToPageAtSpecificTsRef.current &&
      !isInRangeOfArray(currentTime, [
        currentPageTsRange.first!,
        currentPageTsRange.second!,
      ])
    ) {
      console.log(
        `currentTime out of this page range navigating to new page at specific ts! - ${currentTime} not in ${currentPageTsRange.first} - ${currentPageTsRange.second}`
      );
      isNavingToPageAtSpecificTsRef.current = true;
      navToPageAtTs(currentTime);
    }
  }, [currentTime, JSON.stringify(currentPageTsRange)]);

  // *** MULTISELECT STUFF ***
  const getEmptyMultiSelectState = useCallback(
    (): number[][][] => [Array(source.formattedSentences.length).fill([])],
    [JSON.stringify(source.formattedSentences)]
  );

  const multiSelectableBlobGroups: MultiSelectableBlob[][] = useMemo(
    () => [
      source.formattedSentences.map((sentence) =>
        sentence.tokens.map((ytToken) => ytToken.token)
      ),
    ],
    [JSON.stringify(source.formattedSentences)]
  );

  const {
    multiSelectedWords,
    multiSelectTokenIndeces,
    setIsMultiSelectOn,
    closeMultiSelect,
    isMultiSelectOn,
    handleToggleMultiSelect,
    removeWordFromMultiSelect,
  } = useMultiSelect(multiSelectableBlobGroups, getEmptyMultiSelectState);

  const reprocessPageMut = useReprocessYoutubePage();

  //SNIPPET
  const qc = useQueryClient();

  const pageNumber: number = source.pageNb;
  const sourceId: number = source.id;

  const reprocessPage = useCallback(() => {
    reprocessPageMut.mutate(
      { sourceId, pageNb: pageNumber },
      {
        onSuccess: (newPage: YouTubePageView) => {
          updateYoutubeCacheWithPage(sourceId, pageNumber, newPage, qc);
          qc.invalidateQueries(INTERESTING_THINGS_QUERY_KEY);
          qc.invalidateQueries(TAGGED_INSTANCES_QUERY_KEY);
        },
      }
    );
  }, [sourceId, pageNumber, reprocessPageMut]);

  const navLines: NavLine[] = useMemo(
    () =>
      source.formattedSentences.map((l: YouTubeTranscriptLineView) => ({
        tokens: l.tokens.map((t) => ({
          timeStamp: t.timeStamp,
          //TODO: !
          canJumpTo:
            isWordToken(t.token) &&
            (t.token.word!.status === "is_learning" ||
              t.token.word!.status === "unopened"),

          //TODO: not sure isWordToken is correctly used here....?
          isNavable: isWordToken(t.token),
        })),
      })),
    [JSON.stringify(source.formattedSentences)]
  );

  const {
    seekToPrevLine,
    seekToFocusedToken,
    focusPrevJumpableToken,
    focusNextJumpableToken,
    focusPrevLine,
    focusNextLine,
    focusPrevWord,
    focusNextWord,
    focusSpecificToken,
    focusedLineIndex,
    focusedTokenIndex,
  } = useNavigableLines(navLines, currentTime, seekTo);

  const handleKeyEvent = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === "j") {
        focusNextLine();
      } else if (event.key === "k") {
        focusPrevLine();
      } else if (event.key === "w") {
        focusNextWord();
      } else if (event.key === "b") {
        focusPrevWord();
      } else if (event.key === "Enter") {
        seekToFocusedToken();
      } else if (event.key === "n") {
        focusNextJumpableToken();
      } else if (event.key === "N" && event.shiftKey) {
        focusPrevJumpableToken();
      } else if (event.key === "S" && event.shiftKey) {
        seekToPrevLine();
      }
    },
    [
      seekToPrevLine,
      seekToFocusedToken,
      focusPrevJumpableToken,
      focusNextJumpableToken,
      focusPrevLine,
      focusNextLine,
      focusPrevWord,
      focusNextWord,
    ]
  );

  useKeyPressEvent(
    {
      id: "yt-lines",
      priority: 0,
      useCallbackGeneratedCB: handleKeyEvent,
    },
    true
  );

  const playingIndexIshes: PlayingIndexIsh[] | undefined = useMemo(() => {
    if (currentTime === undefined) {
      return undefined;
    }

    return source.formattedSentences
      .map((l, lineIndex) => {
        const matchingTokenIndeces: number[] = l.tokens
          .map((t, tokenIndex) =>
            t.timeStamp === currentTime || t.timeStamp === currentTime - 1
              ? tokenIndex
              : undefined
          )
          .filter((i) => i !== undefined) as number[];
        return matchingTokenIndeces.length > 0
          ? { lineIndex, tokenIndeces: matchingTokenIndeces }
          : undefined;
      })
      .filter((l) => l !== undefined) as PlayingIndexIsh[];
  }, [currentTime, JSON.stringify(source.formattedSentences)]);

  const actionElements = [
    getYouTubeCopyLinkOption(source.videoId),
    {
      label: "Reprocess page",
      onClick: reprocessPage,
    },
  ];

  //TODO: rename appLocation
  const appLocation: YoutubeSourceLocation = {
    type: SourceLocationType.youtube,
    pageNumber,
    sourceId: source.id,
  };

  return (
    <div className="h-full">
      <div>
        <SourceHeader
          sourceName={source.name}
          sourceActions={actionElements}
          sourceType="youtube_transcript"
        />

        <div className="flex gap-2">
          <div className="video-pagination-and-multiselect w-[40%]">
            <EmbeddedYoutubeVideo
              videoId={source.externalId}
              onPlayerReady={onPlayerReady}
              onPlayerStateChange={onPlayerStateChange}
              onPlayerPlay={handlePlayerPlay}
            />
            <div className="flex justify-between p-6">
              <div className="w-1/4 flex justify-center h-full">
                <button
                  className={clsx([
                    "font-bold font-xs text-white bg-blue-500 rounded active:bg-blue-800 w-full h-full align-middle p-1 h-2",
                  ])}
                  onClick={
                    isMultiSelectOn
                      ? closeMultiSelect
                      : () => setIsMultiSelectOn(true)
                  }
                >
                  MS
                </button>
              </div>
              <div className="w-1/4 flex justify-center">
                {currentTime !== undefined && (
                  <span>{formatTimestamp(currentTime)}</span>
                )}
              </div>
              <div className="w-1/2">
                <PaginationControls
                  sourceId={source.id}
                  currentPageNb={currentPageNb!}
                  totalPageNumber={source.totalNbPages}
                  onChangePage={onChangePage}
                />
              </div>
            </div>
            <div>
              {isMultiSelectOn && (
                <MultiSelectMenu
                  wordsSelected={multiSelectedWords}
                  onClickRemove={removeWordFromMultiSelect}
                  onCloseMenu={closeMultiSelect}
                  location={appLocation}
                />
              )}
            </div>
          </div>

          <div className="w-[60%]">
            <YouTubeLines
              isFetching={isFetching || reprocessPageMut.isLoading}
              onLineTsClick={seekTo}
              lines={source.formattedSentences}
              appLocation={appLocation}
              playingLineIshes={playingIndexIshes}
              focusedLineIndex={focusedLineIndex}
              focusedTokenIndex={focusedTokenIndex}
              onTokenHover={focusSpecificToken}
              tokensInMultiSelect={multiSelectTokenIndeces[0]}
              onToggleMultiSelect={({ lineIndex, tokenIndex }) =>
                handleToggleMultiSelect({
                  blobGroupIndex: 0,
                  blobIndex: lineIndex,
                  tokenIndex,
                })
              }
            />
          </div>
        </div>
      </div>
      <PlayBackControls
        onClickPlayPause={playPause}
        onClickBackward={seekToPrevLine}
        isPaused={!isPlayingOrBuffering}
      />
    </div>
  );
};

const Wrapped: React.FC = () => {
  const { sourceId, pageNb: pageNbFromUrl, changePage } = useSourceUrlDetails();
  const qc = useQueryClient();
  const queryKey = getSourceQueryKey(sourceId!, pageNbFromUrl);
  const {
    data: page,
    isFetching,
    isError,
    error,
  } = useGetYoutubePage(
    sourceId!,
    { page: pageNbFromUrl },
    {
      query: {
        queryKey,
        keepPreviousData: true,
      },
    }
  );

  useEffect(() => {
    if (pageNbFromUrl === undefined && page?.pageNb !== undefined) {
      changePage(page.pageNb);
    }
  }, [pageNbFromUrl, page?.pageNb]);

  usePrefetchAdjacentPages({
    pageNb: page?.pageNb,
    totalNbPages: page?.totalNbPages,
    sourceId: sourceId!,
    getQueryKey: getSourceQueryKey,
    fetchSource: getYoutubePage,
  });

  const handleYtNavigateToTs = (ts: number): void => {
    if (page === undefined) {
      throw new Error("this shouldn't happen");
    }

    const cachedPage: YouTubePageView | undefined = qc
      .getQueriesData([getSourceQueryKeyPrefix(page.id)])
      .map((cachedQuery) => cachedQuery[1])
      .map((queryData) => {
        if (!isYoutubeSource(queryData)) {
          throw new Error(
            "this shouldn't happen! - these should all be yt pages!"
          );
        }
        return queryData;
      })
      .filter(isYoutubeSource) // TODO: annoying to have to have this and map above
      .find((cachedYtPage) => isInRange(ts, cachedYtPage.pageTsRange));

    if (cachedPage !== undefined) {
      qc.setQueryData(queryKey, cachedPage);
      changePage(cachedPage.pageNb);
      return;
    }

    getPageForTimestamp(sourceId!, ts)
      .then((data: YouTubePageView) => {
        qc.setQueryData(queryKey, data);
        changePage(data.pageNb);
      })
      .catch(() => {
        throw new Error("this isn't working!");
      });
  };

  useEffect(() => {
    if (page !== undefined) {
      updateLastViewedPage(page.id, { lastViewedPage: page.pageNb });
    }
  }, [page?.pageNb]);

  if (page === undefined) {
    return <CenteredLoading />;
  }

  if (isError) {
    return <ErrorLoading error={error} />;
  }

  return (
    <YouTubePage
      source={page}
      isFetching={isFetching}
      navToPageAtTs={handleYtNavigateToTs}
      currentPageNb={page.pageNb}
      onChangePage={changePage}
    />
  );
};

export default Wrapped;
